import { FC } from "react";
import { Divider, Stack } from "@mui/material";
import { GridApi } from "ag-grid-community";
import { OpportunitiesMetadataCard } from "../../../common/opportunities/components/metadata/OpportunitiesMetadataCard";
import {
  SavingsOpportunity,
  useGetUsersMeOrganisationsCurrentBillingTotalCostQuery,
} from "@/services/cloudchipr.api";
import { useTotalInHeaderCalculator } from "@/components/pages/common/ag-grid/utils/hooks/useTotalInHeaderCalculator.hook";
import { SavingsOpportunitiesTopOpportunities } from "@/components/pages/savings-opportunities/components/metadata/top-opportunities/SavingsOpportunitiesTopOpportunities";

interface SavingsOpportunitiesMetadataProps {
  gridApi: GridApi | null;
}

export const SavingsOpportunitiesMetadata: FC<
  SavingsOpportunitiesMetadataProps
> = ({ gridApi }) => {
  const { data } = useGetUsersMeOrganisationsCurrentBillingTotalCostQuery();

  const monthlySavings = useTotalInHeaderCalculator<SavingsOpportunity>(
    gridApi,
    (data) => data.monthly_savings,
    "sum",
  );
  const estimatedMonthlySavings =
    useTotalInHeaderCalculator<SavingsOpportunity>(
      gridApi,
      (data) => data.estimated_monthly_price,
      "sum",
    );

  const currentMonthlyPrice = useTotalInHeaderCalculator<SavingsOpportunity>(
    gridApi,
    (data) => data.current_monthly_price,
    "sum",
  );

  return (
    <Stack direction="row" p={1} spacing={1} justifyContent="space-between">
      <Stack
        direction="row"
        border={1}
        borderRadius={2}
        borderColor="grey.300"
        flexBasis="60%"
        width="60%"
        divider={<Divider orientation="vertical" flexItem />}
      >
        <OpportunitiesMetadataCard
          title="Billing Cost"
          value={data?.total_cost ?? 0}
          tooltip="Billing cloud cost over the past 30 days."
        />

        <OpportunitiesMetadataCard
          title="Current Monthly Price"
          prefix="~"
          value={currentMonthlyPrice ?? 0}
        />

        <OpportunitiesMetadataCard
          title="Monthly Savings"
          prefix="~"
          value={monthlySavings}
          tooltip="Potential monthly savings"
          valueColor="success.light"
        />

        <OpportunitiesMetadataCard
          title="Estimated Cost After Savings"
          prefix="~"
          value={estimatedMonthlySavings}
          tooltip="Estimated monthly price after implementing the recommendations."
        />
      </Stack>

      <SavingsOpportunitiesTopOpportunities />
    </Stack>
  );
};
